import { Files, Warning } from "@phosphor-icons/react";
import { type JSX, useMemo } from "react";

import type { CsvState } from "../../../types";

interface FileInfoProps {
  uploadState: { csvState: CsvState } & { sourceName: string | "instantly" };
  isFileError: boolean;
}

export default function FileInfo({
  uploadState: { csvState, sourceName },
  isFileError,
}: FileInfoProps) {
  const { missing_data, row_count } = csvState;

  const invalidRowCount = missing_data.length;
  const totalRowCount = row_count;

  const invalidRowsCsvUrl = useMemo(() => {
    if (missing_data?.length) {
      const columns = Object.keys(missing_data[0]);

      let csvContent = `${columns.join(",")}\n`;

      missing_data.forEach((failedRow) => {
        const row = columns
          .map((column) => {
            const value = failedRow[column];
            if (value === null || value === undefined) {
              return "";
            }
            // Convert value to string and escape quotes by doubling them
            const stringValue = value.toString().replace(/"/g, '""');
            // Wrap row in quotes to handle commas
            return `"${stringValue}"`;
          })
          .join(",");

        csvContent += `${row}\n`;
      });
      return URL.createObjectURL(
        new Blob([csvContent], { type: "text/csv;charset=utf-8;" }),
      );
    }
  }, [missing_data]);

  let errorContent: JSX.Element;
  if (isFileError) {
    errorContent = (
      <div className="flex items-center gap-x-0.5 text-red-500">
        <Warning weight="fill" size={16} />

        <p className="text-xs">
          Uploaded file must have valid column names and a valid LinkedIn URL in
          the first row
        </p>
      </div>
    );
  } else if (invalidRowCount) {
    errorContent = (
      <div className="flex items-center gap-x-0.5 text-yellow-500">
        <Warning weight="fill" size={16} />

        <p className="text-xs">
          {invalidRowCount}/{totalRowCount} rows have missing data, make sure to
          add fallbacks for all variables.{" "}
          <a
            href={invalidRowsCsvUrl}
            download="FailedRows.csv"
            className="text-xs-bold underline"
          >
            Export rows
          </a>
        </p>
      </div>
    );
  }

  return (
    <>
      <div className="flex items-center justify-between p-2.5 gap-x-2 border rounded-4xl mb-2.5 border-purple-100 bg-purple-10">
        <div className="border border-black-150 p-1 rounded-4xl">
          <div className="bg-black-100 p-3 rounded-3xl">
            <div className="bg-black-800 text-white rounded-xl p-1.5 shadow-[0_2.571px_5.143px_1.714px_rgba(17,24,39,0.20)]">
              <Files size={16} />
            </div>
          </div>
        </div>

        <div className="flex flex-1 flex-col gap-y-0.5">
          <span className="break-all text-left text-sm-bold">
            {sourceName === "instantly" ? "Instantly" : sourceName}
          </span>
          <span className="text-black-400 text-xs-medium">
            {totalRowCount - invalidRowCount}/{totalRowCount} row
            {totalRowCount > 1 ? "s" : ""} valid
          </span>
        </div>
      </div>

      {errorContent}
    </>
  );
}
